import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ChevronRight, MessageSquare, Download, Loader2, MapPin, Clock, Package, XCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Card, Chip, PageHeader, Button } from "../components/Primitives";
import { myRequestsWithCounts, archiveRequest, reopenRequest } from "../lib/db";

function toCSV(rows) {
  const header = ["ref_code","title","category","material","quantity","budget","deadline","location","status","proposals_total","proposals_pending","proposals_accepted"];
  const lines = [header.join(",")];
  rows.forEach((r) => {
    const cells = [r.id, r.title, r.category, r.material, r.quantity, r.budget, r.deadline, r.location, r.status, r.counts.total, r.counts.pending, r.counts.accepted];
    lines.push(cells.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
  });
  return lines.join("\n");
}

function downloadCSV(name, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

export default function MyRequests() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    if (!user?.id) return;
    const r = await myRequestsWithCounts(user.id);
    setRows(r);
  };

  const doWithdraw = async (e, r) => {
    e.stopPropagation();
    if (busyId || !r?.dbId) return;
    if (!window.confirm(t("myRequests.withdrawConfirm"))) return;
    setBusyId(r.id);
    try {
      await archiveRequest(r.dbId);
      await reload();
      toast.success(t("myRequests.withdrawn"));
    } catch (err) {
      toast.error(err?.message || "Could not withdraw");
    } finally {
      setBusyId(null);
    }
  };

  const doReopen = async (e, r) => {
    e.stopPropagation();
    if (busyId || !r?.dbId) return;
    setBusyId(r.id);
    try {
      await reopenRequest(r.dbId);
      await reload();
      toast.success(t("myRequests.reopened"));
    } catch (err) {
      toast.error(err?.message || "Could not reopen");
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    let alive = true;
    if (!user?.id) { setLoading(false); return; }
    myRequestsWithCounts(user.id).then((r) => { if (alive) { setRows(r); setLoading(false); } }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, [user?.id]);

  const activeRows    = rows.filter((r) => r.status === "open" || r.status === "draft");
  const completedRows = rows.filter((r) => r.status === "closed" || r.status === "completed");
  const archivedRows  = rows.filter((r) => r.status === "archived");
  const list = tab === "active" ? activeRows : tab === "completed" ? completedRows : archivedRows;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto animate-fade-up" data-testid="my-requests-page">
      <PageHeader
        kicker={<>R4Pro · Workspace</>}
        title={t("myRequests.title")}
        subtitle={t("myRequests.subtitle")}
        testId="my-requests-header"
        right={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadCSV("r4pro-my-requests.csv", toCSV(rows))} disabled={rows.length === 0} data-testid="my-requests-export">
              <Download className="w-4 h-4" /> {t("common.exportCsv")}
            </Button>
            <Button onClick={() => navigate("/app/new-request")} data-testid="my-requests-new">
              {t("finder.startCta")}
            </Button>
          </div>
        }
      />

      <div className="flex gap-2 mb-5 border-b border-carbon-600 overflow-x-auto">
        <button onClick={() => setTab("active")} data-testid="my-req-tab-active"
          className={`px-4 py-2 text-sm border-b-2 -mb-px transition whitespace-nowrap ${
            tab === "active" ? "border-electric-500 text-ink-900" : "border-transparent text-ink-600 hover:text-ink-900"
          }`}
        >
          {t("myRequests.active")} <span className="ml-1 text-xs text-ink-500 font-mono">({activeRows.length})</span>
        </button>
        <button onClick={() => setTab("completed")} data-testid="my-req-tab-completed"
          className={`px-4 py-2 text-sm border-b-2 -mb-px transition whitespace-nowrap ${
            tab === "completed" ? "border-electric-500 text-ink-900" : "border-transparent text-ink-600 hover:text-ink-900"
          }`}
        >
          {t("myRequests.completed")} <span className="ml-1 text-xs text-ink-500 font-mono">({completedRows.length})</span>
        </button>
        <button onClick={() => setTab("archived")} data-testid="my-req-tab-archived"
          className={`px-4 py-2 text-sm border-b-2 -mb-px transition whitespace-nowrap ${
            tab === "archived" ? "border-electric-500 text-ink-900" : "border-transparent text-ink-600 hover:text-ink-900"
          }`}
        >
          {t("myRequests.archived")} <span className="ml-1 text-xs text-ink-500 font-mono">({archivedRows.length})</span>
        </button>
      </div>

      {loading ? (
        <div className="text-ink-500 font-mono text-xs py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
      ) : list.length === 0 ? (
        <Card className="p-10 text-center" data-testid="my-requests-empty">
          <FileText className="w-6 h-6 mx-auto text-ink-500 mb-2" />
          <p className="text-ink-700 text-sm">{t("myRequests.emptyTitle")}</p>
          <p className="text-ink-500 text-xs mt-1">{t("myRequests.emptyHelp")}</p>
          <Button className="mt-4" onClick={() => navigate("/app/new-request")} data-testid="my-requests-empty-cta">
            {t("finder.startCta")}
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {list.map((r) => (
            <Card key={r.id} onClick={() => navigate(`/app/requests/${r.id}`)} data-testid={`my-req-${r.id}`} className="p-5 cursor-pointer hover:border-electric-500/40 transition">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-carbon-750 border border-carbon-600 flex flex-col items-center justify-center shrink-0">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-ink-600">REQ</span>
                  <span className="font-mono text-[10px] text-ink-900">{r.id.replace("REQ-","")}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-[10px] text-ink-600">{r.id}</span>
                    <Chip className="!text-[10px] !py-0.5">{r.status?.toUpperCase() || "OPEN"}</Chip>
                    {r.urgent && <Chip className="!bg-red-500/10 !text-red-300 !border-red-500/30 !text-[10px] !py-0.5">{t("requests.urgent")}</Chip>}
                  </div>
                  <h3 className="text-sm font-semibold text-ink-900 leading-snug">{lang === "it" ? r.title : r.titleEn}</h3>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-ink-600">
                    <span className="font-mono flex items-center gap-1"><Package className="w-3 h-3" /> {r.material || "—"}</span>
                    <span className="font-mono">{r.budget}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.deadline || "—"}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.location || "—"}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="text-right">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-electric-600">{t("myRequests.proposals")}</div>
                    <div className="font-mono text-2xl font-bold text-ink-900">{r.counts.total}</div>
                  </div>
                  {r.counts.pending > 0 && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-electric-500/10 border border-electric-500/40 text-electric-600 text-[10px] font-mono">
                      <MessageSquare className="w-3 h-3" /> {r.counts.pending} {t("myRequests.newLabel")}
                    </div>
                  )}
                  {(r.status === "open" || r.status === "draft") && (
                    <button
                      onClick={(e) => doWithdraw(e, r)}
                      disabled={busyId === r.id}
                      data-testid={`my-req-withdraw-${r.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-carbon-600 text-ink-700 hover:border-ink-500 hover:text-ink-900 text-[10px] transition disabled:opacity-50"
                    >
                      <XCircle className="w-3 h-3" /> {t("myRequests.withdraw")}
                    </button>
                  )}
                  {r.status === "archived" && (
                    <button
                      onClick={(e) => doReopen(e, r)}
                      disabled={busyId === r.id}
                      data-testid={`my-req-reopen-${r.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-electric-500/40 text-electric-600 hover:bg-electric-500/10 text-[10px] transition disabled:opacity-50"
                    >
                      <RotateCcw className="w-3 h-3" /> {t("myRequests.reopen")}
                    </button>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-ink-500 self-center" />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
